import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["submit", "input", "error"]
  static values = {
    submitting: { type: Boolean, default: false },
    testMode: { type: Boolean, default: false }
  }

  connect() {
    console.log("Form controller connected to", this.element);

    // Detect test environment
    this.isTestEnvironment =
      window.Capybara ||
      /HeadlessChrome/.test(navigator.userAgent) ||
      this.testModeValue;

    // Add a test attribute to make it easier to verify controller connection
    if (this.isTestEnvironment) {
      this.element.dataset.formControllerConnected = "true";
    }

    // Add input validation listeners if needed
    this.inputTargets.forEach(input => {
      input.addEventListener("blur", this.validateInput.bind(this, input))
    })
  }
  
  // Prevent multiple form submissions
  preventDoubleSubmission(event) {
    if (this.submittingValue) {
      event.preventDefault()
      return
    }
    
    if (this.hasSubmitTarget) {
      // Disable submit button and show loading state
      this.submitTarget.disabled = true
      
      // Store original text if needed for restoration
      if (!this.submitTarget.dataset.originalText) {
        this.submitTarget.dataset.originalText = this.submitTarget.innerText
      }
      
      // Change button text to loading state
      if (this.submitTarget.dataset.loadingText) {
        this.submitTarget.innerText = this.submitTarget.dataset.loadingText
      }
      
      // Add spinner if needed
      if (!this.submitTarget.querySelector(".spinner") && this.submitTarget.dataset.showSpinner !== "false") {
        const spinner = document.createElement("span")
        spinner.className = "spinner inline-block animate-spin ml-2"
        spinner.innerHTML = "⟳" // Simple spinner, replace with proper icon if needed
        this.submitTarget.appendChild(spinner)
      }
    }
    
    this.submittingValue = true
  }
  
  // Reset form to initial state
  resetForm() {
    this.element.reset()
    
    // Clear validation errors
    this.clearErrors()
    
    // Reset submit button
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false
      
      // Restore original text
      if (this.submitTarget.dataset.originalText) {
        this.submitTarget.innerText = this.submitTarget.dataset.originalText
      }
      
      // Remove spinner
      const spinner = this.submitTarget.querySelector(".spinner")
      if (spinner) {
        spinner.remove()
      }
    }
    
    this.submittingValue = false
  }
  
  // Validate a single input
  validateInput(input) {
    // Skip validation if input has no validation rules
    if (!input.hasAttribute("required") && 
        !input.hasAttribute("pattern") && 
        !input.getAttribute("type") === "email") {
      return true
    }
    
    // Clear previous errors
    this.clearErrorFor(input)
    
    // Required field validation
    if (input.hasAttribute("required") && !input.value.trim()) {
      this.showErrorFor(input, "This field is required")
      return false
    }
    
    // Email validation
    if (input.getAttribute("type") === "email" && input.value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(input.value)) {
        this.showErrorFor(input, "Please enter a valid email address")
        return false
      }
    }
    
    // Pattern validation
    if (input.hasAttribute("pattern") && input.value) {
      const pattern = new RegExp(input.getAttribute("pattern"))
      if (!pattern.test(input.value)) {
        const errorMsg = input.dataset.patternError || "Please enter a valid value"
        this.showErrorFor(input, errorMsg)
        return false
      }
    }
    
    return true
  }
  
  // Validate entire form
  validateForm() {
    let isValid = true
    
    this.inputTargets.forEach(input => {
      if (!this.validateInput(input)) {
        isValid = false
      }
    })
    
    return isValid
  }
  
  // Show error message for an input
  showErrorFor(input, message) {
    // Find error element by ID or create one
    let errorElement = this.getErrorElementFor(input)
    
    if (!errorElement) {
      errorElement = document.createElement("div")
      errorElement.className = "error-message text-red-500 text-sm mt-1"
      errorElement.id = `error-${input.id || input.name}`
      input.parentNode.appendChild(errorElement)
    }
    
    errorElement.textContent = message
    input.classList.add("border-red-500")
  }
  
  // Clear error for a specific input
  clearErrorFor(input) {
    const errorElement = this.getErrorElementFor(input)
    if (errorElement) {
      errorElement.textContent = ""
    }
    
    input.classList.remove("border-red-500")
  }
  
  // Get error element for an input
  getErrorElementFor(input) {
    // Try to find by ID first
    if (input.id) {
      return document.getElementById(`error-${input.id}`)
    }
    
    // Try to find by name
    if (input.name) {
      return document.getElementById(`error-${input.name}`)
    }
    
    // Find nearest error target
    return input.closest("[data-form-target='input']")?.querySelector("[data-form-target='error']")
  }
  
  // Clear all errors
  clearErrors() {
    this.errorTargets.forEach(error => {
      error.textContent = ""
    })
    
    this.inputTargets.forEach(input => {
      input.classList.remove("border-red-500")
    })
  }
  
  // Submit form with validation
  submit(event) {
    if (!this.validateForm()) {
      event.preventDefault();
      return;
    }

    // Continue with form submission
    this.preventDoubleSubmission(event);
  }

  // New method for direct form submission
  submitForm(event) {
    // Validate form
    if (!this.validateForm()) {
      event.preventDefault();
      return;
    }

    // Prevent double submission
    this.preventDoubleSubmission(event);

    // Form will be submitted normally - Turbo will handle it
  }

  // Handle native form submission (non-Turbo)
  handleNativeSubmit(event) {
    console.log("Native form submission triggered", event);

    // Log form data being submitted
    if (event.target && event.target.tagName === 'FORM') {
      const formData = new FormData(event.target);
      console.log("Form data in native submit:");
      for (let [key, value] of formData.entries()) {
        if (typeof value === 'string' && value.length > 500) {
          console.log(`${key}: [Large data - ${value.length} chars]`);
        } else {
          console.log(`${key}: ${value}`);
        }
      }

      // Add a debug version of form submission to see if it works
      if (event.target.getAttribute('data-debug-submit') === 'true') {
        console.log("Debug form submission enabled, submitting via FormData/fetch");
        event.preventDefault();

        // Create and submit via fetch API
        fetch(event.target.action, {
          method: 'POST',
          body: formData,
          headers: {
            'Accept': 'text/vnd.turbo-stream.html, text/html, application/json'
          },
          credentials: 'same-origin'
        })
        .then(response => {
          console.log("Form submitted via fetch:", response);
          if (response.redirected) {
            window.location.href = response.url;
          } else {
            return response.text();
          }
        })
        .then(html => {
          if (html) {
            console.log("Response HTML:", html.substring(0, 200) + '...');
            // Process any Turbo streams in the response
            if (window.Turbo && typeof window.Turbo.renderStreamMessage === 'function') {
              window.Turbo.renderStreamMessage(html);
            }
          }
        })
        .catch(error => {
          console.error("Error submitting form:", error);
        });
      }
    }
  }

  // Handle form submission start (called by turbo:submit-start)
  handleSubmitStart(event) {
    console.log("Form submission start handler called", event);

    // Check the current form configuration
    console.log("Form submission details:", {
      form: this.element,
      id: this.element.id,
      action: this.element.action,
      method: this.element.method,
      turboEnabled: this.element.getAttribute('data-turbo') !== 'false',
      acceptsFormat: this.element.querySelector('input[name="format"]')?.value || 'none specified',
      turboFrame: this.element.getAttribute('data-turbo-frame')
    });

    // Ensure Turbo is enabled for this form
    if (this.element.getAttribute('data-turbo') === 'false') {
      console.log("Turbo was disabled for this form, enabling it");
      this.element.setAttribute('data-turbo', 'true');
    }

    // Disable submit button to prevent double submission
    this.preventDoubleSubmission(event);

    // Log the form data being submitted (for debugging)
    const formData = new FormData(this.element);
    console.log("Form data being submitted:");
    for (let [key, value] of formData.entries()) {
      // Don't log huge values like details
      if (typeof value === 'string' && value.length > 500) {
        console.log(`${key}: [Large data - ${value.length} chars]`);
      } else {
        console.log(`${key}: ${value}`);
      }
    }
  }

  // Handle successful submission with appropriate feedback
  // Called by Turbo after submission completes
  handleSubmitSuccess(event) {
    console.log("Form submission success handler called", event);
    console.log("Response status:", event.detail?.fetchResponse?.response?.status);

    // Check if there was a response
    if (event.detail?.fetchResponse) {
      const response = event.detail.fetchResponse.response;
      console.log("Response received:", response.status, response.statusText);

      // Handle redirects
      if (response.redirected) {
        console.log("Redirect detected, turbo should navigate to:", response.url);

        // Force redirect in case Turbo doesn't handle it
        setTimeout(() => {
          // If we're still on the same page after 100ms, force a redirect
          if (window.location.href !== response.url) {
            console.log("Turbo didn't handle redirect, doing it manually");
            window.location.href = response.url;
          }
        }, 100);
      }
    }

    // For regular form submissions (not turbo streams targeting specific elements)
    // we should reset the form or redirect
    const isTurboStreamResponse = event.detail?.fetchResponse?.response?.headers?.get('Content-Type')?.includes('text/vnd.turbo-stream');

    if (!isTurboStreamResponse) {
      console.log("Not a Turbo Stream response - resetting form");

      // Show success message
      const alertDiv = document.createElement('div');
      alertDiv.className = 'alert alert-success';
      alertDiv.textContent = 'Directory Listing was successfully created.';
      this.element.parentNode.insertBefore(alertDiv, this.element);

      // Reset the form
      this.resetForm();

      // Redirect to the listing page
      if (this.element.action.includes('directory_listings')) {
        // Extract the directory ID from the URL
        const directoryIdMatch = this.element.action.match(/directories\/([^\/]+)/);
        if (directoryIdMatch && directoryIdMatch[1]) {
          const directoryId = directoryIdMatch[1];
          const listingUrl = `/panel/directories/${directoryId}/directory_listings`;
          console.log("Redirecting to listing page:", listingUrl);

          // Redirect after a short delay to allow user to see the success message
          setTimeout(() => {
            window.location.href = listingUrl;
          }, 500);
        }
      }
    }

    // Notify test framework that submission completed successfully
    if (this.isTestEnvironment) {
      this.element.dataset.submissionState = "success";
      console.log("Test environment detected - form submission successful");
    }

    // Dispatch a custom event that tests can listen for
    const submissionEvent = new CustomEvent("form:submission-success", {
      bubbles: true,
      detail: { formId: this.element.id }
    });
    this.element.dispatchEvent(submissionEvent);
  }

  // Handle form submission error (called by turbo:submit-end with error response)
  handleSubmitError(event) {
    console.log("Form submission error handler called");

    if (this.isTestEnvironment) {
      this.element.dataset.submissionState = "error";
    }

    // Re-enable the submit button and restore its state
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false;

      if (this.submitTarget.dataset.originalText) {
        this.submitTarget.innerText = this.submitTarget.dataset.originalText;
      }

      const spinner = this.submitTarget.querySelector(".spinner");
      if (spinner) {
        spinner.remove();
      }
    }

    this.submittingValue = false;
  }
}